import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, ShoppingBag } from "lucide-react";
import { NAV, BRAND } from "../../data/content";
import { useOrder } from "../../context/OrderContext";
import { useCart } from "../../context/CartContext";

export const Header = () => {
  const { openOrder } = useOrder();
  const { count, openCheckout } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
  }, [menuOpen]);

  const go = (href) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        className={`fixed inset-x-0 top-0 z-[100] transition-all duration-300 ${
          scrolled ? "glass border-b border-gold/15" : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 h-[76px] sm:h-[88px]">
          <a href="#top" onClick={(e) => { e.preventDefault(); go("#top"); }} className="flex items-center gap-3" data-testid="header-logo">
            <img src={BRAND.logo} alt="Smokey & Creamy" className="h-14 w-14 sm:h-16 sm:w-16 rounded-full object-cover ring-2 ring-gold/50 drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]" />
            <span className="hidden sm:block text-gold-gloss font-display text-xl font-semibold leading-none">
              Smokey &amp; Creamy
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-9">
            {NAV.map((n) => (
              <button
                key={n.href}
                onClick={() => go(n.href)}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className="relative text-sm font-medium text-cream/75 hover:text-cream transition-colors group"
              >
                {n.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <button
              onClick={openCheckout}
              aria-label="View cart"
              data-testid="header-cart"
              className="relative grid h-11 w-11 place-items-center rounded-full border border-white/12 text-cream/80 hover:text-gold hover:border-gold/40 transition-colors"
            >
              <ShoppingBag size={18} />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-gold px-1 text-[11px] font-bold text-ink">{count}</span>
              )}
            </button>
            <a
              href={BRAND.phoneHref}
              aria-label="Call the restaurant"
              data-testid="header-call"
              className="hidden sm:grid h-11 w-11 place-items-center rounded-full border border-white/12 text-cream/80 hover:text-gold hover:border-gold/40 transition-colors"
            >
              <Phone size={17} />
            </a>
            <button
              onClick={openOrder}
              data-testid="header-order-now"
              className="hidden sm:inline-flex items-center rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-ink transition-transform hover:-translate-y-0.5"
            >
              Order Now
            </button>
            <button
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-open"
              className="md:hidden h-11 w-11 grid place-items-center rounded-full border border-white/12 text-cream"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed inset-0 z-[110] bg-surface md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 h-[68px] border-b border-white/10">
              <span className="text-gold-gloss font-display text-lg font-semibold">Smokey &amp; Creamy</span>
              <button
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
                className="h-11 w-11 grid place-items-center rounded-full border border-white/12 text-cream"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex flex-col px-6 pt-6">
              {NAV.map((n, i) => (
                <motion.button
                  key={n.href}
                  onClick={() => go(n.href)}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i + 0.05 }}
                  className="border-b border-white/8 py-5 text-left font-display text-3xl font-semibold text-cream"
                >
                  {n.label}
                </motion.button>
              ))}
              <motion.button
                onClick={() => { setMenuOpen(false); openOrder(); }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                data-testid="mobile-order-now"
                className="mt-8 rounded-full bg-gold py-4 text-center text-base font-semibold text-ink"
              >
                Order Now
              </motion.button>
              <a
                href={BRAND.phoneHref}
                className="mt-3 flex items-center justify-center gap-2 rounded-full border border-white/12 py-4 text-base font-semibold text-cream"
              >
                <Phone size={18} /> {BRAND.phoneDisplay}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
